import React, { useState } from 'react';
import { useNavigate, useLoaderData, useSearchParams } from 'react-router-dom';
import { BearerToken } from '@marageti/z4-sdk/lib/auth';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import {
  hasAuthorizedRole,
  LocalStorageService,
  useApiClient,
  useFetchImmutable,
} from '@marageti/z4-lib';
import { FieldValues } from 'react-hook-form';
import { GoogleLoginRequest } from '@marageti/z4-sdk/lib/auth/Token';
import { TimeZoneMap } from '@marageti/z4-sdk/lib/standards';
import ActivateView from './activate-view';
import ConfirmPhoneView from './confirm-phone-view';
import { useAuthStore } from '../../store';
import { authorizedRoles, getMatchingUser } from '../../utils/auth';

const DEFAULT_VALUES = {
  phone: {
    prefix: '',
    number: '',
  },
  timeZone: {
    name: '',
    rawOffsetInMinutes: 0,
  },
};

const LocalStorage = new LocalStorageService();

const ActivateContainer = () => {
  const {
    loggedInTraveler,
    removeAuthenticatedTraveler,
    setIsAuthenticated,
    setAuthenticatedTraveler,
  } = useAuthStore();
  const apiClient = useApiClient();
  const navigate = useNavigate();

  const tokenExists = useLoaderData();
  const [search, setSearch] = useSearchParams();
  const email = search.get('email');
  const token = search.get('token');
  const redirect = search.get('redirect');
  const tripRequestId = search.get('tripRequestId');
  const tripRequestDestination = tripRequestId ? `/trip-requests/${tripRequestId}` : undefined;
  const timezones = useFetchImmutable<TimeZoneMap>(apiClient.standardsClient.getTimeZones, [], true);
  const timezonesMap = timezones.data ? new Map(Object.entries(timezones.data)) : new Map();
  const suggestedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ');
  const suggestedTimezoneOption = timezonesMap.has(suggestedTimezone) ? {
    name: suggestedTimezone,
    rawOffsetInMinutes: timezonesMap.get(suggestedTimezone),
  }
    : DEFAULT_VALUES.timeZone;

  const [loginError, setLoginError] = useState<ApiError>();
  const [tokenExpired, setTokenExpired] = useState(!tokenExists || !email);
  const [newLinkSent, setNewLinkSent] = useState(false);
  const [showConfirmPhone, setShowConfirmPhone] = useState(false);

  const onActivationSuccess = (bearerToken: BearerToken): void => {
    const { accessToken } = bearerToken;
    if (email) {
      LocalStorage.addToLocalStorage('accessToken', accessToken);
      LocalStorage.addToLocalStorage('email', email);
      apiClient.instance.setAuthToken(accessToken);

      apiClient.peopleClient
        .getTraveler(email)
        .then((data: Traveler) => {
          const matchingUser = getMatchingUser(data.email, data.userInfo);
          if (!matchingUser || !hasAuthorizedRole(matchingUser.roles, authorizedRoles, true)) {
            throw new Error('Not Authorized');
          }
          search.delete('email');
          search.delete('token');
          setSearch(search);
          setAuthenticatedTraveler(data);
          setShowConfirmPhone(true);
        })
        .catch((e: ApiError) => {
          removeAuthenticatedTraveler();
          setIsAuthenticated(false);
          setLoginError(e);
        });
    }
  };
  // Username and password activation and login
  const onPasswordSubmit = (data: FieldValues) => {
    if (email && token) {
      setLoginError(undefined);
      return apiClient.authClient
        .putUserResetToken(email, token, { password: data.password })
        .then((res) => {
          onActivationSuccess(res);
        })
        .catch(() => {
          setTokenExpired(true);
        });
    }
    return setTokenExpired(true);
  };

  const onPhoneSubmit = (data: FieldValues) => {
    if (loggedInTraveler) {
      const { phone, timeZone } = data;
      const request = {
        ...loggedInTraveler,
        ...phone.number && { phone: data.phone },
        ...timeZone.name && { timeZone: data.timeZone },
      };
      return apiClient.peopleClient.putTraveler(loggedInTraveler.id, request)
        .then((res: Traveler) => {
          setAuthenticatedTraveler(res);
        })
        .catch(console.error)
        .finally(() => {
          setIsAuthenticated(true);
          navigate(tripRequestDestination || redirect || '/');
        });
    }
    // This point will not be reached if the traveler was not logged in when setting a password
    setIsAuthenticated(true);
    return navigate(tripRequestDestination || redirect || '/');
  };

  // For Google SSO logging in will activate the user
  const onGoogleSSOSubmit = (loginRequest: GoogleLoginRequest): void => {
    setLoginError(undefined);
    apiClient.authClient.login(loginRequest)
      .then(onActivationSuccess)
      .catch((e: ApiError) => {
        setLoginError(e);
      });
  };

  const onResendLinkSuccess = () => {
    setNewLinkSent(true);
  };

  if (showConfirmPhone) {
    return (
      <ConfirmPhoneView
        defaultValues={{
          phone: loggedInTraveler?.phone || DEFAULT_VALUES.phone,
          timeZone: loggedInTraveler?.timeZone.name ? loggedInTraveler?.timeZone : suggestedTimezoneOption,
        }}
        onSubmit={onPhoneSubmit}
      />
    );
  }

  return (
    <ActivateView
      email={email!}
      loginError={loginError}
      newLinkSent={newLinkSent}
      onGoogleSSOSubmit={onGoogleSSOSubmit}
      onPasswordSubmit={onPasswordSubmit}
      onResendLinkSuccess={onResendLinkSuccess}
      tokenExpired={tokenExpired}
    />
  );
};

export default ActivateContainer;
